import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { DesktopService } from '../../services/desktopService';
import useAuthStore from '../../store/useAuthStore';
import useChatStore from '../../store/useChatStore';
import { db } from '../../lib/firebase';
import { doc, onSnapshot, collection, query, where, orderBy } from 'firebase/firestore';

const CommandIcon = ({ className }) => <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M8 9l3 3-3 3m5 0h3M5 20h14a2 2 0 002-2V6a2 0 00-2-2H5a2 0 00-2 2v12a2 0 002 2z" /></svg>;
const SendIcon = ({ className }) => <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M13 5l7 7-7 7M5 5l7 7-7 7" /></svg>;

const ITermControl = ({ targetUid, targetDeviceId, nodeId, folderPath, deviceData }) => {
    const [text, setText] = useState('');
    const [isSending, setIsSending] = useState(false);
    
    const [sessions, setSessions] = useState([]);
    const [activeSessionIndex, setActiveSessionIndex] = useState(0);
    
    const { activeChat } = useChatStore();
    const { user } = useAuthStore();
    const inputRef = useRef(null);

    // Auto-focus input on mount
    useEffect(() => {
        if (inputRef.current) {
            inputRef.current.focus();
        }
    }, [activeSessionIndex]);

    // Stream sessions from subcollection
    useEffect(() => {
        if (!targetUid || !targetDeviceId || !nodeId) return;

        const sessionsRef = collection(db, 'users', targetUid, 'devices', targetDeviceId, 'iterm_sessions');
        const q = query(sessionsRef, where('nodeId', '==', nodeId), orderBy('index', 'asc'));

        const unsubscribe = onSnapshot(q, (snapshot) => {
            const fetchedSessions = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));
            
            setSessions(fetchedSessions);
            
            // If we have sessions, find the active one
            if (fetchedSessions.length > 0) {
                const active = fetchedSessions.find(s => s.isActive);
                if (active) {
                    setActiveSessionIndex(active.index);
                }
            } else {
                // If NO sessions exist, proactively create the first one
                const timer = setTimeout(() => {
                    handleNewSession();
                }, 1000); // Small delay to avoid race on mount
                return () => clearTimeout(timer);
            }
        });

        return () => unsubscribe();
    }, [targetUid, targetDeviceId, nodeId]);

    const sendCommand = async (payload) => {
        setIsSending(true);
        try {
            const currentSession = sessions.find(s => s.index === activeSessionIndex);
            
            const payloadData = {
                command: 'run_iterm_control',
                nodeId: nodeId,
                folderPath: folderPath,
                target_ide: 'iterm',
                session_index: activeSessionIndex,
                ...payload
            };

            // Only add IDs if they exist to avoid Firebase "undefined" error
            if (currentSession?.sessionId) payloadData.sessionId = currentSession.sessionId;
            if (currentSession?.windowId) payloadData.windowId = currentSession.windowId;
            
            await DesktopService.sendDesktopCommand(
                targetUid,
                targetDeviceId,
                payloadData,
                activeChat?.id
            );
        } catch (error) {
            console.error("Failed to send command to iTerm:", error);
        } finally {
            setIsSending(false);
            if (inputRef.current) inputRef.current.focus();
        }
    };

    const handleSend = async (e) => {
        if (e) e.preventDefault();
        if (!text.trim() || isSending) return;

        const commandText = text.trim();
        setText('');
        await sendCommand({ text: commandText });
    };

    const handleCtrlC = () => sendCommand({ text: 'c', is_control_key: true });

    const handleNewSession = async () => {
        await sendCommand({ new_session: true });
        // State updates will be handled purely by the firestore listener
    };

    const handleClearSessions = async () => {
        if (window.confirm("Are you sure you want to close ALL iTerm sessions for this node? This cannot be undone.")) {
            await sendCommand({ clear_sessions: true });
        }
    };

    return (
        <div className="p-6 bg-[var(--bg-secondary)] border-t border-[var(--border-strong)]">
            <div className="max-w-4xl mx-auto space-y-4">
                {/* Session Selector & Tools */}
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        {sessions.length > 0 ? sessions.map((session, index) => (
                            <button
                                key={session.id || index}
                                onClick={async () => {
                                    setActiveSessionIndex(session.index);
                                    await sendCommand({ text: '', session_index: session.index });
                                }}
                                className={`px-3 py-1.5 rounded-lg text-[10px] font-bold transition-all duration-300 ${activeSessionIndex === session.index ? 'bg-[var(--accent-primary)] text-[var(--bg-main)] shadow-lg shadow-[var(--accent-primary)]/20' : 'bg-[var(--bg-tertiary)] text-[var(--text-dim)] hover:bg-[var(--bg-tertiary)]/80'}`}
                            >
                                TERMINAL {session.index + 1}
                            </button>
                        )) : (
                            <div className="px-3 py-1.5 rounded-lg text-[10px] font-bold bg-[var(--bg-tertiary)] text-[var(--text-dim)] animate-pulse">
                                INITIALIZING...
                            </div>
                        )}
                        <button
                            onClick={handleNewSession}
                            title="Open New iTerm Window"
                            className="p-1.5 rounded-lg bg-[var(--bg-tertiary)] text-[var(--text-dim)] hover:text-[var(--accent-primary)] transition-colors"
                        >
                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 4v16m8-8H4" /></svg>
                        </button>
                        <button
                            onClick={handleClearSessions}
                            title="Clear All Sessions"
                            className="p-1.5 rounded-lg bg-[var(--bg-tertiary)] text-[var(--text-dim)] hover:text-red-500 hover:bg-red-500/10 transition-colors ml-1"
                        >
                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                        </button>
                    </div>

                    <button
                        onClick={handleCtrlC}
                        disabled={isSending}
                        className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-red-500/10 text-red-500 hover:bg-red-500/20 transition-all font-bold text-[10px] uppercase tracking-wider"
                    >
                        <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
                        Ctrl + C
                    </button>
                </div>

                <form onSubmit={handleSend} className="relative group">
                    <div className="absolute inset-0 bg-[var(--accent-primary)]/5 blur-2xl rounded-3xl opacity-0 group-focus-within:opacity-100 transition-opacity duration-500" />

                    <div className="relative flex items-center gap-3 bg-[var(--bg-main)] p-2 rounded-2xl border border-[var(--border-strong)] shadow-lg focus-within:border-[var(--accent-primary)]/50 transition-all duration-300">
                        <div className="flex items-center justify-center w-10 h-10 rounded-xl bg-green-500/10 text-green-500 shrink-0">
                            <CommandIcon className="w-5 h-5" />
                        </div>

                        <input
                            ref={inputRef}
                            type="text"
                            value={text}
                            onChange={(e) => setText(e.target.value)}
                            placeholder={`Type a command for Terminal ${activeSessionIndex + 1}...`}
                            disabled={isSending}
                            className="flex-1 bg-transparent border-none outline-none text-sm font-medium text-[var(--text-main)] placeholder:text-[var(--text-dim)] py-2"
                        />

                        <button
                            type="submit"
                            disabled={!text.trim() || isSending}
                            className={`flex items-center justify-center w-10 h-10 rounded-xl transition-all duration-300 ${text.trim() && !isSending ? 'bg-[var(--accent-primary)] text-[var(--bg-main)] shadow-lg shadow-[var(--accent-primary)]/20 scale-100' : 'bg-[var(--bg-tertiary)] text-[var(--text-dim)] scale-95 opacity-50'}`}
                        >
                            <SendIcon className="w-4 h-4" />
                        </button>
                    </div>

                    <div className="mt-3 flex items-center justify-between px-2">
                        <div className="flex items-center gap-2">
                            <span className="flex h-1.5 w-1.5 rounded-full bg-green-500 animate-pulse" />
                            <span className="text-[10px] font-bold text-[var(--text-muted)] tracking-widest uppercase">Connected to iTerm</span>
                        </div>
                        <span className="text-[10px] font-medium text-[var(--text-dim)]">Press Enter to send</span>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default ITermControl;
